/**
 * Check divergences between a theoretical recipe and a real execution history.
 *
 * @param {Array} theoretical - Ordered recipe definition.
 *   Each item: { pool_id: number, duration_min: number, duration_max: number }
 *
 * @param {Array} history - List of real steps (may be in progress).
 *   Each item: { pool_id: number, value: number, done: boolean, start_ts: number }
 *
 * @returns {{
 *   has_error: boolean,
 *   steps_error: Array<{
 *     id: number,
 *     reason: 'duration_too_long' | 'duration_too_short' | 'missing_step' | 'unexpected_step',
 *     details: object
 *   }>
 * }}
 */
export const checkProcessDivergences = (theoretical, history) => {
  // Work on a sorted copy to avoid mutating original history
  const sortedHistory = [...history].sort((a, b) => a.start_ts - b.start_ts);

  const stepsError = [];

  // Helper: check duration constraints for a (theoretical, real) pair
  const checkDuration = (stepIndex, theoreticalStep, historyStep) => {
    const { duration_min, duration_max, pool_id } = theoreticalStep;
    const { value, done } = historyStep;

    if (value > duration_max) {
      stepsError.push({
        id: stepIndex,
        reason: 'duration_too_long',
        details: {
          pool_id,
          value,
          duration_max
        }
      });
    }

    if (done === true && value < duration_min) {
      stepsError.push({
        id: stepIndex,
        reason: 'duration_too_short',
        details: {
          pool_id,
          value,
          duration_min
        }
      });
    }
  };

  // Helper: find next theoretical index with given pool_id after fromIndex
  const findNextIndexWithPool = (poolId, fromIndex) => {
    for (let i = fromIndex; i < theoretical.length; i++) {
      if (theoretical[i].pool_id === poolId) {
        return i;
      }
    }
    return -1;
  };

  let ti = 0; // index in theoretical
  let hi = 0; // index in history

  // Main alignment loop while we still have both theoretical and real steps
  while (ti < theoretical.length && hi < sortedHistory.length) {
    const tStep = theoretical[ti];
    const hStep = sortedHistory[hi];

    if (tStep.pool_id === hStep.pool_id) {
      // Order is respected for this step → check durations
      checkDuration(ti, tStep, hStep);
      ti += 1;
      hi += 1;
      continue;
    }

    // Order mismatch: try to realign using lookahead in theoretical
    const realPoolId = hStep.pool_id;
    const k = findNextIndexWithPool(realPoolId, ti + 1);

    if (k !== -1) {
      // We found the same pool later in the theoretical recipe.
      // All steps between ti and k-1 are missing (skipped).
      for (let missingIndex = ti; missingIndex < k; missingIndex++) {
        stepsError.push({
          id: missingIndex,
          reason: 'missing_step',
          details: {
            expected_pool_id: theoretical[missingIndex].pool_id,
            pool_id: realPoolId
          }
        });
      }

      // Now align history[hi] with theoretical[k]
      const alignedTheoreticalStep = theoretical[k];
      checkDuration(k, alignedTheoreticalStep, hStep);

      // Move past the aligned step
      ti = k + 1;
      hi += 1;
    } else {
      // The real pool does not exist further in the theoretical recipe.
      // This is an unexpected step at this point.
      stepsError.push({
        id: ti, // index of the first step that is not in the right place
        reason: 'unexpected_step',
        details: {
          pool_id: realPoolId,
          expected_pool_id: theoretical[ti] ? theoretical[ti].pool_id : null
        }
      });

      // We consume only this real step and keep ti (still expecting tStep)
      hi += 1;
    }
  }

  // If there are remaining real steps while the recipe is fully consumed,
  // all remaining real steps are unexpected.
  while (hi < sortedHistory.length && ti >= theoretical.length) {
    const hStep = sortedHistory[hi];

    stepsError.push({
      id: ti, // index just after the last valid theoretical step
      reason: 'unexpected_step',
      details: {
        pool_id: hStep.pool_id,
        expected_pool_id: null
      }
    });

    hi += 1;
  }

  // We do NOT mark remaining theoretical steps as missing here,
  // because the process may simply still be in progress.

  return {
    has_error: stepsError.length > 0,
    steps_error: stepsError
  };
};

/* ------------------------------------------------------------------ */
/* Example with the exact scenario you gave (for sanity check):       */
/*
const theoretical = [
  { pool_id: 4,  duration_min: 90, duration_max: 120 }, // 0
  { pool_id: 5,  duration_min: 5,  duration_max: 60  }, // 1
  { pool_id: 6,  duration_min: 30, duration_max: 120 }, // 2
  { pool_id: 7,  duration_min: 30, duration_max: 120 }, // 3
  { pool_id: 8,  duration_min: 30, duration_max: 120 }, // 4
  { pool_id: 9,  duration_min: 30, duration_max: 120 }, // 5
  { pool_id: 15, duration_min: 30, duration_max: 120 }, // 6
  { pool_id: 16, duration_min: 30, duration_max: 120 }, // 7
  { pool_id: 18, duration_min: 30, duration_max: 120 }, // 8
];

const history = [
  { pool_id: 4,  value: 100, done: 1, start_ts: 1000 },
  { pool_id: 5,  value: 80,  done: 1, start_ts: 1100 },
  { pool_id: 6,  value: 40,  done: 1, start_ts: 1200 },
  { pool_id: 9,  value: 40,  done: 1, start_ts: 1300 },
  { pool_id: 16, value: 40,  done: 1, start_ts: 1400 },
  { pool_id: 17, value: 40,  done: 1, start_ts: 1500 },
  { pool_id: 18, value: 40,  done: 1, start_ts: 1600 },
];

console.log(JSON.stringify(checkProcessDivergences(theoretical, history), null, 2));
*/
/* Expected steps_error:
[
  {
    id: 1,
    reason: 'duration_too_long',
    details: { pool_id: 5, value: 80, duration_max: 60 }
  },
  {
    id: 3,
    reason: 'missing_step',
    details: { expected_pool_id: 7, pool_id: 9 }
  },
  {
    id: 4,
    reason: 'missing_step',
    details: { expected_pool_id: 8, pool_id: 9 }
  },
  {
    id: 6,
    reason: 'missing_step',
    details: { expected_pool_id: 15, pool_id: 16 }
  },
  {
    id: 8,
    reason: 'unexpected_step',
    details: { pool_id: 17, expected_pool_id: 18 }
  }
]
*/

/**
 * Returns the theoretical step index AND step object corresponding
 * to the last step of a given cart history.
 *
 * @param {Array} cartHistory - The list of executed steps for a cart.
 * @param {Array} theoretical - The ordered theoretical process.
 * @returns {{ theoreticalStepIndex: number|null, theoreticalStep: object|null }}
 */
export const getTheoreticalMatchFromCartHistory = (cartHistory, theoretical) => {
  if (!cartHistory || cartHistory.length === 0) {
    return { theoreticalStepIndex: null, theoreticalStep: null };
  }

  const lastStep = cartHistory[cartHistory.length - 1];
  const realPoolId = lastStep.pool_id;

  // Count previous occurrences of this pool before the last step
  const previousOccurrences = cartHistory
    .slice(0, -1)
    .filter(s => s.pool_id === realPoolId)
    .length;

  // Now find the matching theoretical occurrence
  let occurrenceCounter = 0;

  for (let ti = 0; ti < theoretical.length; ti++) {
    const t = theoretical[ti];

    if (t.pool_id === realPoolId) {
      if (occurrenceCounter === previousOccurrences) {
        return {
          theoreticalStepIndex: ti,
          theoreticalStep: t
        };
      }
      occurrenceCounter++;
    }
  }

  return { theoreticalStepIndex: null, theoreticalStep: null };
};

/**
 * Returns the next theoretical step after the current one
 * based on the cartHistory. Returns null if already at the last step.
 *
 * @param {Array} cartHistory - List of steps already executed.
 * @param {Array} theoretical - Full theoretical recipe.
 * @returns {{ nextTheoreticalStepIndex: number|null, nextTheoreticalStep: object|null }}
 */
export const getNextTheoreticalStep = (cartHistory, theoretical) => {
  if (!cartHistory || cartHistory.length === 0) {
    return { nextTheoreticalStepIndex: 0, nextTheoreticalStep: theoretical[0] };
  }

  const { theoreticalStepIndex } = getTheoreticalMatchFromCartHistory(cartHistory, theoretical);

  // If no match found → cannot determine next
  if (theoreticalStepIndex == null) {
    return { nextTheoreticalStepIndex: null, nextTheoreticalStep: null };
  }

  const nextIndex = theoreticalStepIndex + 1;

  // If we reached or exceeded the end → no next step
  if (nextIndex >= theoretical.length) {
    return { nextTheoreticalStepIndex: null, nextTheoreticalStep: null };
  }

  return {
    nextTheoreticalStepIndex: nextIndex,
    nextTheoreticalStep: theoretical[nextIndex]
  };
};